/**
 * A descrição de uma atividade, nos quatro formatos em que ela existe.
 *
 * O campo `description` de `activities` chega ao frontend em QUATRO formas
 * diferentes, e nenhum consumidor pode escolher qual recebe:
 *
 *  1. `null`         — atividade criada pelo fluxo "Minhas trilhas" sem descrição.
 *                      `ActivityCreator` manda `""`, o middleware
 *                      `ConvertEmptyStringsToNull` do Laravel (`app/Http/Kernel.php`)
 *                      transforma em `null` e `ActivityController@insert` grava assim.
 *  2. `"null"`       — a STRING de quatro letras, resultado de `JSON.stringify(null)`.
 *                      É o que atividade e avaliação gravam até hoje quando ninguém
 *                      escreve descrição, e há acervo inteiro assim.
 *  3. texto plano    — acervo legado, anterior ao editor.
 *  4. JSON de nós    — o formato do editor (Slate):
 *                      `[{ "type": "paragraph", "children": [{ "text": "..." }] }]`.
 *
 * Antes deste módulo, cada consumidor decidia sozinho — e todos decidiam do mesmo
 * jeito frágil, `description.substring(0, 1) === "["`, que estoura com o formato 1
 * ("Cannot read properties of null") e exibe "null" literal na tela no formato 2.
 * Era o mesmo defeito copiado em cinco arquivos.
 *
 * A regra aqui é: NUNCA estourar por causa do dado, porque o professor não tem como
 * consertar a descrição de uma atividade cuja LISTA não abre.
 */

/** O que aparece na tela quando não há descrição nenhuma para mostrar. */
export const DESCRICAO_AUSENTE = "Sem descrição";

/**
 * O tipo de nó usado no fallback do editor.
 *
 * O acervo tem `"paragaph"` (sem o "r", em `ActivityCreator`, `EditNodeModal` e
 * `DailyRecord`) e `"paragraph"` (em `SlateEditor/Editor.jsx`). Os dois renderizam
 * IGUAL: nenhum dos dois tem `case` no switch de `SlateUtilityFunctions.js`, então
 * ambos caem no `default`, que devolve uma `<div>`. Por isso o padrão aqui é o certo,
 * e quem precisa preservar o valor gravado passa o seu.
 */
export const TIPO_PARAGRAFO = "paragraph";

/**
 * Um nó vazio do editor — o mínimo que o Slate aceita como valor.
 *
 * Slate quebra com array vazio, então "sem conteúdo" precisa ser um parágrafo com
 * texto vazio, e não `[]`.
 */
const noVazio = tipo => [{ type: tipo, children: [{ text: "" }] }];

/**
 * O texto cru, já descartado o que significa "não há descrição".
 *
 * Devolve `""` para `null`, `undefined`, string vazia, só-espaços, para a string
 * `"null"` (formato 2) e para qualquer coisa que não seja string — um número ou um
 * objeto inesperado não deve derrubar a tela.
 */
const textoCru = valor => {
  if (typeof valor !== "string") return "";
  const aparado = valor.trim();
  // Só `"null"` entra nesta lista, e por medição: é o que `JSON.stringify(null)`
  // produz e o que está gravado no acervo. `"undefined"` não foi observado em
  // nenhum caminho de gravação, então não é tratado — inventar formato aqui
  // esconderia uma descrição legítima.
  //
  // Esta linha é REDUNDANTE, e de propósito: `JSON.parse("null")` devolve `null`,
  // que não é nó nem string, então o formato 2 também morreria no ramo de lixo
  // estrutural de `descricaoEmTextoPlano`. Medido com a suíte de mutação — remover
  // esta linha não muda resultado nenhum. Ela fica porque a alternativa é o
  // comportamento certo depender de um detalhe silencioso do `JSON.parse`, e porque
  // as duas defesas são cobertas por testes distintos (ver a suíte).
  return aparado === "null" ? "" : aparado;
};

/** O nó tem a cara de um nó do editor? Folha tem `text`; elemento tem `children`. */
const pareceNoDoEditor = no =>
  !!no &&
  typeof no === "object" &&
  (typeof no.text === "string" || Array.isArray(no.children));

/**
 * Analisa a string e diz o que ela é.
 *
 * Substitui `description.substring(0, 1) === "["`, e é melhor que ele por um caso
 * concreto: a descrição `"[Revisão] Frações"` começa com "[" e NÃO é JSON — o teste
 * antigo mandava essa string para o `JSON.parse` (que estoura) e, no caminho da
 * lista, escondia a descrição do professor. Aqui o parse é a autoridade.
 *
 * Os dois motivos de falha são distinguidos de propósito, porque pedem respostas
 * opostas: o que NÃO analisa é texto do professor e deve aparecer na tela; o que
 * analisa mas não é nó (`"[]"`, `"[1,2]"`, `"42"`) é lixo estrutural, e mostrar `[]`
 * numa célula de tabela é pior que não mostrar nada.
 *
 * O `catch` é silencioso de propósito, e não contradiz a regra de deixar rastro em
 * erro engolido: aquela vale para chamada de rede, onde o status HTTP é a pista que
 * se perde. Aqui não há rede nem pista a perder — formato inválido é uma das
 * entradas ESPERADAS, e o retorno é a resposta, não uma falha.
 *
 * @returns {{ehJson: boolean, valor: *, nos: Array|null}}
 */
const analisarDescricao = cru => {
  let valor;
  try {
    valor = JSON.parse(cru);
  } catch (erro) {
    return { ehJson: false, valor: null, nos: null };
  }

  const nos =
    // Array vazio é rejeitado junto com o resto: `[]` analisa, mas não é conteúdo, e
    // como valor do Slate é pior que inútil (quebra o editor).
    Array.isArray(valor) &&
    valor.length > 0 &&
    // `every`, e não `some`: `[1, 2, 3]` analisa como array e não é nó de editor
    // nenhum. Deixar passar produziria texto vazio e um valor que o Slate rejeita.
    valor.every(pareceNoDoEditor)
      ? valor
      : null;

  return { ehJson: true, valor, nos };
};

/**
 * Todo o texto de um nó do editor, descendo pelos `children`.
 *
 * Recursivo porque o nó pode ser aninhado — uma lista, uma tabela ou um trecho em
 * negrito colocam folhas vários níveis abaixo, e uma descrição inteira dentro de uma
 * tabela não pode virar "Sem descrição".
 */
const textoDoNo = no => {
  if (typeof no === "string") return no;
  if (!no || typeof no !== "object") return "";
  if (typeof no.text === "string") return no.text;
  if (Array.isArray(no.children)) return no.children.map(textoDoNo).join("");
  return "";
};

/**
 * Junta os nós de topo com espaço (senão dois parágrafos viram "OláMundo"); as folhas
 * dentro de um nó já vieram juntas sem separador, porque são o mesmo parágrafo
 * partido por formatação.
 */
const textoDosNos = nos =>
  nos.map(textoDoNo).join(" ").replace(/\s+/g, " ").trim();

/**
 * A descrição em texto plano, para uma célula de tabela ou um resumo.
 *
 * Junta TODOS os nós, e é o segundo defeito que isso corrige: o código anterior
 * fazia `children.map(des => { item.descrição = des.text })`, ou seja sobrescrevia a
 * variável a cada volta e ficava com o ÚLTIMO nó. Uma descrição com formatação
 * ("Frações **equivalentes**") são três folhas, e o professor via só a última —
 * quando a última era vazia, via "Sem descrição" com a descrição preenchida.
 *
 * @param {*} descricao O campo `description` como veio da API.
 * @param {string} vazio O que devolver quando não há texto nenhum.
 * @returns {string} Sempre uma string, em qualquer entrada.
 */
export const descricaoEmTextoPlano = (descricao, vazio = DESCRICAO_AUSENTE) => {
  // O editor também aceita o array já analisado, então ele pode chegar aqui assim.
  if (Array.isArray(descricao)) return textoDosNos(descricao) || vazio;

  const cru = textoCru(descricao);
  if (!cru) return vazio;

  const { ehJson, valor, nos } = analisarDescricao(cru);
  if (nos) return textoDosNos(nos) || vazio;

  if (ehJson) {
    // Analisou, mas não é nó de editor. String ainda é texto aproveitável
    // (`JSON.stringify("Frações")`); número, objeto e `[]` não são nada.
    return typeof valor === "string" ? valor.trim() || vazio : vazio;
  }

  // Texto plano legado — incluindo o `"[Revisão] Frações"` que não analisa. Devolver
  // o cru mostra a descrição do professor; devolver `vazio` a esconderia.
  return cru;
};

/**
 * A descrição no formato que o editor consome — sempre um array de nós válido.
 *
 * @param {*} descricao O campo `description` como veio da API.
 * @param {string} tipo O `type` do nó de fallback. Passe o valor que o chamador já
 *   gravava para não mudar o JSON que vai ao banco (ver `TIPO_PARAGRAFO`).
 * @returns {Array} Sempre um array com pelo menos um nó.
 */
export const descricaoParaEditor = (descricao, tipo = TIPO_PARAGRAFO) => {
  if (Array.isArray(descricao)) {
    return descricao.length > 0 ? descricao : noVazio(tipo);
  }

  const cru = textoCru(descricao);
  if (!cru) return noVazio(tipo);

  const { nos } = analisarDescricao(cru);
  if (nos) return nos;

  // Texto plano legado entra como o conteúdo de um parágrafo — o mesmo que o
  // `ActivityCreator` sempre fez. A diferença é que agora `null` e `"null"` não
  // chegam aqui, então o editor deixa de abrir com a palavra "null" escrita dentro.
  // Um texto que não é descrição (`"[]"`, `"42"`) cai no nó vazio via
  // `descricaoEmTextoPlano`, que é o que decide se há texto.
  const texto = descricaoEmTextoPlano(cru, "");
  return texto ? [{ type: tipo, children: [{ text: texto }] }] : noVazio(tipo);
};

/**
 * A descrição está no formato do editor?
 *
 * Existe porque alguns chamadores precisam saber se devem REESCREVER o campo antes
 * de entregá-lo ao `<RichText>`, e não só ler o texto.
 */
export const temFormatoDeEditor = descricao => {
  if (Array.isArray(descricao)) return descricao.length > 0;
  const cru = textoCru(descricao);
  return !!cru && analisarDescricao(cru).nos !== null;
};
